"use client";

// Tell every entitlement-sensitive surface to re-read itself, after a change
// this tab performed.
//
// WHY THIS IS SHARED: the trial → Pro conversion is mounted in three places
// (the app-wide TrialConvertHost, the Settings → Billing card, and the project
// page). Each owns its own state, so a conversion started in one left the
// others on trial data until a hard refresh. One implementation, so a fourth
// mount cannot diverge the same way.
//
// WHY A ONE-SHOT BROADCAST IS NOT ENOUGH (the reported bug):
// POST /api/billing/trial/convert returns as soon as the $29 has SETTLED, but
// it deliberately leaves `trial_status='active'` in the database — the flip to
// 'converted', and the 300-credit grant, happen later in the
// invoice.payment_succeeded webhook. The route only arms a backstop (it pulls
// `trial_ends_at` forward to now) so that the NEXT read of
// /api/billing/overview or /api/billing/trial/status runs
// reconcileTrialConversion against Stripe and self-heals.
//
// So at the instant the modal hands back, the authoritative Pro state may not
// exist yet, and a single re-read races the webhook: it can come back still
// saying `isTrialing: true`, which is exactly the "Trial active · 100/100 ·
// ends in 3 days" the QA report saw sitting next to a Pro plan. Hence
// syncEntitlementUntilSettled() below — a bounded, single-flight poll that
// keeps re-reading (each read also drives the server-side reconcile) and
// re-broadcasting until the app holds the authoritative post-trial state.

/**
 * Fired at trial surfaces that own their own state. TrialStatusCard self-fetches
 * /api/billing/trial/status on mount and had no other way to learn the trial had
 * ended.
 */
export const TRIAL_STATE_EVENT = "panda:trial-state-changed";

/**
 * Fired at surfaces that own a /api/billing/overview snapshot — today the
 * Settings → Billing "Current plan" card, which had NO listener at all: a
 * conversion driven from the app-wide host while the user sat on Billing
 * updated the trial banner and the nav chip around it and left the plan card
 * itself on trial state.
 *
 * `detail.overview` carries the snapshot the sync loop already fetched, so a
 * listener applies it directly instead of issuing a duplicate request — and so
 * a round whose read FAILED stays silent rather than telling every listener to
 * go re-fetch a failing endpoint. The next round retries.
 */
export const BILLING_STATE_EVENT = "panda:billing-state-changed";

/**
 * Fired when the post-purchase sync starts and stops, so a surface can show a
 * short "syncing your plan…" state instead of silently rendering stale data
 * while the webhook lands. `detail.settled` is true only when the sync ended
 * because the authoritative state arrived (false when it ran out of attempts).
 */
export const ENTITLEMENT_SYNC_EVENT = "panda:entitlement-sync";

/**
 * Delay BEFORE each poll round. The first is 0 so the common case — webhook
 * already landed by the time the modal closed — costs exactly one request and
 * settles immediately; the rest back off to ~32s total, which comfortably
 * covers normal Stripe webhook propagation without hammering an endpoint that
 * itself talks to Stripe.
 */
export const ENTITLEMENT_SYNC_BACKOFF_MS: readonly number[] = [
  0, 1_500, 2_500, 4_000, 6_000, 8_000, 10_000,
];

/** The fields of the billing overview this module reasons about. */
export interface EntitlementSnapshot {
  plan?: string;
  isTrialing?: boolean;
  paymentIssue?: boolean;
  [key: string]: unknown;
}

export interface EntitlementSyncResult {
  /** True when a read came back holding the authoritative post-change state. */
  settled: boolean;
  /** Poll rounds actually performed (1 when it settled on the first read). */
  attempts: number;
  /** The last overview successfully read, or null if every read failed. */
  snapshot: EntitlementSnapshot | null;
}

export interface EntitlementSyncOptions {
  /**
   * When this returns true the state is authoritative and polling stops.
   * Defaults to "no longer trialing", the trial → Pro conversion case.
   */
  isSettled?: (snapshot: EntitlementSnapshot) => boolean;
  /** Override the backoff schedule (tests, or a caller with a different SLA). */
  backoffMs?: readonly number[];
}

/**
 * The nav chip's update channel. It self-fetches ONCE on mount and then moves
 * only when this fires — so a purchase that grants after that first read is
 * invisible until the event arrives, or the user reloads the page. Named here
 * rather than written as a literal at each dispatch site because "who is
 * allowed to forget to send it" is the whole failure mode.
 */
export const CREDIT_UPDATE_EVENT = "panda:credit-update";

/** Credit fields the nav chip understands — see components/credit-counter.tsx. */
interface CreditPayload {
  credits_remaining: number;
  credits_total?: number;
  plan?: string;
  resets_at?: string | null;
  daily_used?: number;
  daily_limit?: number | null;
  has_card?: boolean;
  credits_on_hold?: number;
  trial?: boolean;
  monthly_allocation?: number | null;
}

function dispatch(type: string, detail?: unknown): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    detail === undefined ? new Event(type) : new CustomEvent(type, { detail }),
  );
}

/**
 * Normalise an /api/credits body into the chip's event payload.
 *
 * Returns null unless `credits_remaining` is a real number: the chip's
 * panda:credit-update listener dereferences it unguarded, so a bare event — or
 * one carrying a failed read — throws inside the listener and leaves the chip
 * on stale credits. Verify first, then broadcast.
 */
export function toCreditPayload(body: unknown): CreditPayload | null {
  const d = body as Record<string, unknown> | null | undefined;
  if (!d || typeof d.credits_remaining !== "number") return null;
  // Forwarded field-by-field rather than spread: the chip applies whatever it
  // is handed, and `credits_total` in particular is the difference between the
  // nav reading "300/300 credits" and "300/100" — it kept the trial denominator
  // because this event never carried a total at all.
  return {
    credits_remaining: d.credits_remaining,
    ...(typeof d.credits_total === "number"
      ? { credits_total: d.credits_total }
      : {}),
    ...(typeof d.plan === "string" ? { plan: d.plan } : {}),
    ...(d.resets_at !== undefined
      ? { resets_at: d.resets_at as string | null }
      : {}),
    ...(typeof d.daily_used === "number" ? { daily_used: d.daily_used } : {}),
    ...(d.daily_limit !== undefined
      ? { daily_limit: d.daily_limit as number | null }
      : {}),
    ...(typeof d.has_card === "boolean" ? { has_card: d.has_card } : {}),
    ...(typeof d.credits_on_hold === "number"
      ? { credits_on_hold: d.credits_on_hold }
      : {}),
    // The ACTIVE-trial flag. Without it a just-converted user's chip still
    // routes clicks to the "convert your trial" modal they already paid to
    // leave, and its tooltip still calls the balance "trial credits".
    ...(typeof d.trial === "boolean" ? { trial: d.trial } : {}),
    ...(d.monthly_allocation !== undefined
      ? { monthly_allocation: d.monthly_allocation as number | null }
      : {}),
  };
}

/**
 * Tell every entitlement surface about a snapshot the caller ALREADY read.
 *
 * The reported bug in its second form: a checkout return confirmed "You're on
 * the Pro trial" in a modal while the nav chip beside it still read "50/50
 * credits", because the return path decided off /api/credits and then told
 * nobody. The snapshot was right there. Returns whether the event fired.
 */
export function broadcastCredits(body: unknown): boolean {
  const payload = toCreditPayload(body);
  if (!payload) return false;
  dispatch(CREDIT_UPDATE_EVENT, payload);
  return true;
}

/** Read /api/credits and normalise it. Null on any failure. */
async function readCredits(): Promise<CreditPayload | null> {
  try {
    const res = await fetch("/api/credits", { cache: "no-store" });
    if (!res.ok) return null;
    return toCreditPayload(await res.json());
  } catch {
    return null;
  }
}

async function readOverview(): Promise<EntitlementSnapshot | null> {
  try {
    const res = await fetch("/api/billing/overview", { cache: "no-store" });
    if (!res.ok) return null;
    const json = (await res.json()) as EntitlementSnapshot;
    // A 200 carrying { error } (the route's own failure body) is not a snapshot.
    return json && typeof json === "object" && !("error" in json) ? json : null;
  } catch {
    return null;
  }
}

// ── Post-purchase sync ────────────────────────────────────────────────────

let inFlight: Promise<EntitlementSyncResult> | null = null;
/**
 * A COUNT, not a flag.
 *
 * The latch used to be raised by exactly one thing — the poll below — so a
 * boolean was enough. It has a second holder now: a checkout return, which
 * waits for the purchase to appear BEFORE any poll starts. Those overlap (a
 * return that runs out of patience hands off to the poll), and with a boolean
 * whichever finished first would lower the latch out from under the other.
 */
let syncHolds = 0;
const syncListeners = new Set<() => void>();

/** Snapshot for useSyncExternalStore — stable identity, so a boolean. */
export function isEntitlementSyncing(): boolean {
  return syncHolds > 0;
}

export function subscribeEntitlementSync(onChange: () => void): () => void {
  syncListeners.add(onChange);
  return () => {
    syncListeners.delete(onChange);
  };
}

/**
 * Raise the latch for work this module does not run itself, and hand back the
 * release.
 *
 * THE GAP THIS CLOSES: a PayPal trial return took ~30s to appear, and for all
 * of it the app rendered the pre-purchase account as fact — a nav chip
 * asserting "50/50 credits" to someone who had just paid, with nothing on
 * screen to say a purchase was being confirmed. The waiting was already
 * correct; it was simply invisible.
 *
 * The release is idempotent, so a caller that fires it twice (a `finally` plus
 * an early return) cannot strand the latch down while another holder is still
 * working. Pass `settled` when the caller knows the authoritative state
 * arrived; it rides the stop event, and only the LAST holder's answer is
 * announced, because until then nothing has stopped.
 */
export function beginEntitlementSync(): (settled?: boolean) => void {
  let released = false;
  syncHolds += 1;
  if (syncHolds === 1) notifySyncChange(false);
  return (settled = false) => {
    if (released) return;
    released = true;
    syncHolds -= 1;
    if (syncHolds === 0) notifySyncChange(settled);
  };
}

function notifySyncChange(settled: boolean): void {
  // Copied before iterating: a listener that unsubscribes itself (a component
  // unmounting on the state change it just heard about) must not mutate the set
  // mid-iteration.
  for (const listener of [...syncListeners]) {
    try {
      listener();
    } catch {
      // One bad subscriber must not strand the rest — or the `finally` that
      // clears the in-flight latch.
    }
  }
  dispatch(ENTITLEMENT_SYNC_EVENT, { syncing: isEntitlementSyncing(), settled });
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

async function runSync(
  isSettled: (snapshot: EntitlementSnapshot) => boolean,
  backoffMs: readonly number[],
): Promise<EntitlementSyncResult> {
  let snapshot: EntitlementSnapshot | null = null;
  let attempts = 0;
  let settled = false;

  try {
    for (const delay of backoffMs) {
      if (delay > 0) await sleep(delay);
      attempts += 1;

      // In parallel: neither read depends on the other, and the round should
      // cost one round-trip of latency rather than two. Both endpoints run
      // reconcileTrialConversion-adjacent self-healing server-side, so polling
      // them is what ADVANCES the conversion, not merely what observes it.
      const [overview, credits] = await Promise.all([
        readOverview(),
        readCredits(),
      ]);

      if (credits) dispatch(CREDIT_UPDATE_EVENT, credits);
      // Trial-owned surfaces re-read /api/billing/trial/status themselves; that
      // endpoint reconciles too, and reports `finalizing` in exactly this
      // window, which is the "short syncing state" the trial card renders.
      dispatch(TRIAL_STATE_EVENT);

      if (overview) {
        snapshot = overview;
        // Handed to the listener so it applies this snapshot instead of firing
        // a duplicate request. Only dispatched when we actually have one — a
        // failed read must not make the card re-fetch a failing endpoint.
        dispatch(BILLING_STATE_EVENT, { overview });
        if (isSettled(overview)) {
          settled = true;
          break;
        }
      }
    }
  } catch {
    // Defensive: every read above already swallows its own failures, so this
    // can only be something exotic (a listener throwing out of dispatch). The
    // caller is post-payment either way — never surface it as a failure.
  }

  return { settled, attempts, snapshot };
}

/**
 * Poll entitlement until the app holds the authoritative post-change state,
 * broadcasting every round so all surfaces converge together.
 *
 * SINGLE-FLIGHT. The conversion can be triggered from ~22 entry points, and the
 * Billing card may react to the same conversion at the same moment; without
 * this, each would start its own poll loop against a Stripe-backed endpoint.
 * Concurrent callers join the run already in progress.
 *
 * Never throws and never rejects: every caller runs this AFTER money has
 * moved, so a failure here must not surface as a failed purchase. The worst
 * case is the pre-existing behaviour — a surface that updates on next mount.
 */
export function syncEntitlementUntilSettled(
  options: EntitlementSyncOptions = {},
): Promise<EntitlementSyncResult> {
  if (typeof window === "undefined") {
    return Promise.resolve({ settled: false, attempts: 0, snapshot: null });
  }
  if (inFlight) return inFlight;

  const isSettled =
    options.isSettled ?? ((s: EntitlementSnapshot) => s.isTrialing !== true);
  const backoffMs = options.backoffMs ?? ENTITLEMENT_SYNC_BACKOFF_MS;

  const release = beginEntitlementSync();
  const run = runSync(isSettled, backoffMs).then((result) => {
    // Cleared BEFORE the stop notification: a listener that reacts by starting
    // another sync (a second purchase in the same session) must not be handed
    // back this finished run.
    inFlight = null;
    // Whether the poll reached the authoritative state rides the stop event —
    // but only if this was the last holder. While a checkout return is still
    // waiting, nothing has stopped and nothing is announced.
    release(result.settled);
    return result;
  });
  inFlight = run;
  return run;
}

/** Test-only reset of the module-level single-flight latch. */
export function __resetEntitlementSyncForTests(): void {
  inFlight = null;
  syncHolds = 0;
  syncListeners.clear();
}
